import React from 'react';
import {
  Row,
  Col,
  Select,
  message,
  Popover,
} from 'antd';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { PlusOutlined, WarningOutlined } from '@ant-design/icons';

import {
  fieldOption,
} from './formFieldsHelpers';
import { TIME_TYPE_FLAG } from '../../../constants/Flags';
import Colors from '../../../constants/Colors';
import { dataTypesAllowedInMultiDivisionTable } from '../../forms.constants';
import OptionalRow from './OptionalRow';
import LabourTablePreview from './LabourTablePreview';
import CustomSavedFormTablePreview from './CustomSavedFormTablePreview';
import MaterialTablePreview from './MaterialTablePreview';
import ChangeOrderTablePreview from './ChangeOrderTablePreview';
import ShiftTablePreview from './ShiftTable/ShiftTablePreview';
import CustomTablePreview from './CustomTablePreview';
import EquipmentTablePreview from './EquipmentTable/EquipmentTablePreview';
import TimeEntryTablePreview from './TimeEntryTable/TimeEntryTablePreview';
import CostCodeTablePreview from './CostCodeTable/CostCodeTablePreview';
import ScheduleOfValuesTablePreview from './ScheduleOfValuesTablePreview';

import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import HoverHelp from '../../../common/HoverHelp';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';

import TableFieldColumnCard from './TableFieldColumnCard';
import SimpleTextInputModal from '../../../common/modals/SimpleTextInputModal';
import CustomColumnModal from './CustomColumnModal';
import TimeEntryColumnsDragDrop from './TableField/TimeEntryColumnsDragDrop';
import TimeEntryGroupedColumnSelect from './TableField/TimeEntryGroupedColumnSelect';
import {
  createCustomTextColumn,
  validateCustomTextColumnName,
} from './tableColumnHelpers';

import FieldTriggerCheckbox from './FieldTriggerCheckbox';
import ConditionalRenderingRow from './ConditionalRenderingRow';
import HideIfEmptyInPDFCheckbox from './HideIfEmptyInPDFCheckbox';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import DropdownFieldSubDataTypeSelect from './DropdownFieldSubDataTypeSelect';
import { getLinkOptions } from './Attribute.helpers';
import AttributeTablePreview from './AttributeTablePreview';
import { TablePreload } from './TablePreload/TablePreload';
import OnTraccrSwitch from '../../../common/inputs/OnTraccrSwitch';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import TableAutofill from './TableAutofill';
import TableScheduleReminder from './TableScheduleReminder';
import { getBucketTemplateTableColumns } from '../../formHelpers';
import BucketTablePreview from './BucketTablePreview';
import TableFieldTitle from './TableField/TableFieldTitle';

const types = [
  {
    key: 'Materials',
    title: 'Materials',
    description: 'User can select from existing Materials',
  },
  {
    key: 'Labour',
    title: 'Labour Hours',
    description: 'User can select from existing shifts and labour rates',
  },
  {
    key: 'Custom',
    title: 'Custom',
    description: 'User can enter custom data into the table',
  },
  {
    key: 'ChangeOrder',
    title: 'Change Orders',
    description: 'User can select from existing Change Orders',
  },
  {
    key: 'Shifts',
    title: 'Shifts',
    description: 'User can add one or more shifts',
  },
  {
    key: 'TimeEntry',
    title: 'Time Entry',
    description: 'User can enter manual time entries',
  },
  {
    key: 'Equipment',
    title: 'Equipment',
    description: 'User can select from existing Equipment',
    hasSubDataType: true,
    subDataTypePlaceholder: 'All',
    subDataTypeSelectMode: 'multiple',
  },
  {
    key: 'CostCodes',
    title: 'Cost Codes',
    description: 'User can select from existing Cost Codes',
  },
  {
    key: 'Attribute',
    title: 'Attribute',
    description: 'Displays multi-value attributes in a table format',
  },
  {
    key: 'ScheduleOfValues',
    title: 'Schedule of Values',
    description: 'User can add schedule of values',
  },
];

const bucketType = {
  key: 'Buckets',
  title: 'Buckets',
  description: 'User can select from existing Buckets',
  hasSubDataType: true,
  subDataTypePlaceholder: 'Select Bucket Type',
  subDataTypeSelectMode: 'single',
};

const validCustomFieldTypes = new Set([
  'yes-no',
  'dropdown',
  'text',
  'dateTime',
  'attribute',
  'staticText',
  'gpsLocation',
  'table',
  'dynamicAttribute',
]);

const materialOptions = {
  name: 'Name',
  description: 'Description',
  partNumber: 'Part Number',
  units: 'Units',
  price: 'Material Price',
  labourPrice: 'Labour Price',
  quantity: 'Quantity',
  currentQuantity: 'Current Quantity',
  quantityAllocated: 'Quantity Allocated', // [2024-10-04] Not related to field of same name from material object
  quantityRemaining: 'Quantity Remaining',
  location: 'Location',
  toLocation: 'To Location',
  debitOrCredit: 'Debit/Credit',
  addedDate: 'Date Added',
  updatedDate: 'Date Updated',
  cost: 'Material Cost',
  labourCost: 'Labour Cost',
  total: 'Total Material Cost',
  labourCostTotal: 'Total Labour Cost',
  labourAndMaterialTotal: 'Material Total',
  labourPriceTotal: 'Total Labour Price',
  notes: 'Notes',
  supplier: 'Supplier',
  vendor: 'Vendor',
  costcode: 'Cost Code',
  sourceForm: 'Source Form',
  attachments: 'Attachments',
};

const labourOptions = {
  name: 'Name',
  rate: 'Rate',
  quantity: 'Quantity',
  total: 'Total',
  user: 'User',
  position: 'Position',
};

const changeOrderOptions = {
  itemNumber: 'Item Number',
  description: 'Description',
  contractAmount: 'Contract Amount',
  percentageComplete: 'Percentage Complete',
  progressToDate: 'Progress To Date',
  previousBillings: 'Previous Billings',
  invoiceAmount: 'Invoice Amount',
};

const shiftOptions = {
  title: 'Title',
  dates: 'Dates',
  users: 'Users',
  description: 'Description',
  attachments: 'Attachments',
};

const getTimeEntryOptions = (t, customFields, showExcludeAutoBreak, showNightShift, showTimeTypeColumns) => ({
  user: 'User',
  date: 'Date',
  type: 'Type',
  division: 'Division',
  project: t('Project'),
  phase: 'Phase',
  costcode: 'Cost Code',
  local: 'Union Local',
  class: 'Work Classification',
  sageShift: 'Sage Shift',
  hourBased: 'Hour Based',
  time: 'Time',
  note: 'Note',
  status: 'Status',
  enteredVia: 'Entered Via',
  formattedDuration: 'Duration',
  formattedStartTime: 'Start Time',
  formattedEndTime: 'End Time',
  hourlyBillingRate: 'Cost Code Hourly Billing Rate',
  ...(showTimeTypeColumns
    ? {
      regularBillingRate: 'Regular Billing Rate',
      otBillingRate: 'OT Billing Rate',
      dtBillingRate: 'DT Billing Rate',
    }
    : {}),
  userWage: 'User Wage',
  hourlyCost: 'Hourly Cost',
  dailyCost: 'Daily Cost',
  ...showExcludeAutoBreak
    ? {
      excludeFromAutoBreak: 'Exclude from Auto Break Calculations',
    }
    : {},
  ...showNightShift
    ? {
      isNightShift: 'Night Shift',
    }
    : {},
  ...showTimeTypeColumns
    ? {
      regularHours: 'Regular Hours',
      otHours: 'OT Hours',
      doubleOTHours: 'Double OT Hours',
    }
    : {},
  ...customFields,
});

const equipmentOptions = {
  name: 'Name',
  code: 'ID',
  hours: 'Hours',
  days: 'Days',
  weeks: 'Weeks',
  months: 'Months',
  hourlyCost: 'Hourly Cost',
  dailyCost: 'Daily Cost',
  weeklyCost: 'Weekly Cost',
  monthlyCost: 'Monthly Cost',
  hourlyBillingRate: 'Hourly Billing Rate',
  dailyBillingRate: 'Daily Billing Rate',
  weeklyBillingRate: 'Weekly Billing Rate',
  monthlyBillingRate: 'Monthly Billing Rate',
  totalHourlyCost: 'Total Hourly Cost',
  totalDailyCost: 'Total Daily Cost',
  totalWeeklyCost: 'Total Weekly Cost',
  totalMonthlyCost: 'Total Monthly Cost',
  totalHourlyBilling: 'Total Hourly Billing',
  totalDailyBilling: 'Total Daily Billing',
  totalWeeklyBilling: 'Total Weekly Billing',
  totalMonthlyBilling: 'Total Monthly Billing',
  totalCost: 'Total Cost',
  totalBilling: 'Total Billing',
  addedDate: 'Date Added',
  updatedDate: 'Date Updated',
  currentProjectId: 'Location',
  toProjectId: 'To Location',
  notes: 'Notes',
  bucketPath: 'Bucket Path',
  vendor: 'Vendor',
  costcode: 'Cost Code',
  sourceForm: 'Source Form',
  mileage: 'Mileage',
  currentHours: 'Current Hours',
  currentMileage: 'Current Mileage',
  newHoursThreshold: 'Hours Threshold',
  newMileageThreshold: 'Mileage Threshold',
  attachments: 'Attachments',
};

const excludedEquipmentAttributeColumns = new Set([
  'attachments',
  'hours',
  'days',
  'weeks',
  'months',
]);

const scheduleOfValuesOptions = {
  number: 'Number',
  description: 'Description',
  contractAmount: 'Contract Amount',
  percentageComplete: '% Complete',
  totalChanges: 'Total Changes',
  progressToDate: 'Progress To Date',
  previousBillings: 'Previous Billings',
  invoice: 'Invoice',
  notes: 'Notes',
  unitType: 'Unit Type',
  estimatedQuantity: 'Estimated Quantity',
  previouslyBilledQuantity: 'Previously Billed Quantity',
  billableQuantity: 'Billable Quantity',
  completedQuantity: 'Completed Quantity',
  quantityToDate: 'Quantity To Date',
  quantityRemaining: 'Quantity Remaining',
  billingCode: 'Billing Code',
};

const getCostCodeOptions = (t) => ({
  project: t('Project'),
  costcode: 'Cost Code',
  date: 'Date',
  description: 'Description',
  quantity: 'Quantity',
  rate: 'Rate',
  amount: 'Amount',
  hours: 'Hours',
});

const getAttributeOptions = ((t, attrType, attribute, equipmentCustomFields) => {
  switch (attrType) {
    case 'Project': {
      if (attribute === 'equipment') {
        const attributeEquipmentOptions = Object.fromEntries(
          Object.entries(equipmentOptions).filter(([key]) => (
            !excludedEquipmentAttributeColumns.has(key)
          )),
        );
        return {
          ...attributeEquipmentOptions,
          ...equipmentCustomFields,
        };
      }
      return {};
    }
    default: {
      return {};
    }
  }
});

const getOptionsMap = ({
  t,
  timeEntryCustomFields,
  equipmentCustomFields,
  attrType,
  attribute,
  showExcludeAutoBreak,
  showNightShift,
  showTimeTypeColumns,
  relevantBucketTemplate,
}) => ({
  Materials: materialOptions,
  Labour: labourOptions,
  ChangeOrder: changeOrderOptions,
  Shifts: shiftOptions,
  TimeEntry: getTimeEntryOptions(t, timeEntryCustomFields, showExcludeAutoBreak, showNightShift, showTimeTypeColumns),
  Equipment: equipmentOptions,
  CostCodes: getCostCodeOptions(t),
  Attribute: getAttributeOptions(t, attrType, attribute, equipmentCustomFields),
  Buckets: getBucketTemplateTableColumns({ data: relevantBucketTemplate }),
  ScheduleOfValues: scheduleOfValuesOptions,
});

const getCustomFieldsForSection = (acc, customField) => {
  if (!customField) return;
  const { hidden, fields: ourFields, name: sectionName } = customField;
  if (hidden) return;
  ourFields?.forEach?.((subField) => {
    const {
      configProps: subFieldConfig = {},
      selectedType: subFieldType,
    } = subField;
    if (!validCustomFieldTypes.has(subFieldType)) return;
    if (subFieldType === 'table') {
      const {
        columns: subFieldCols = [],
      } = subFieldConfig;
      subFieldCols?.forEach?.((subFieldCol) => {
        const colTitle = `${subFieldConfig.title} - ${subFieldCol.name}`;
        const label = `${sectionName} - ${colTitle}`;
        acc[`${subField.id}-${subFieldCol.key}`] = {
          label,
          title: colTitle,
        };
      });
      return;
    }
    const label = `${sectionName} - ${subFieldConfig.title}`;
    acc[subField.id] = {
      label,
      title: subFieldConfig.title,
    };
  });
};

const getCustomFieldOptions = ({
  customFields,
  selectedDivisionId,
}) => (
  Object.values(customFields)?.reduce((acc, divCustomFields) => {
    const { fields, divisionId } = divCustomFields;
    if (selectedDivisionId && selectedDivisionId !== divisionId) return acc;

    fields?.forEach?.((customField) => {
      getCustomFieldsForSection(acc, customField);
    });
    return acc;
  }, {})
);

const getEquipmentCustomFieldOptions = (equipmentTypes) => (
  equipmentTypes.reduce((acc, type) => {
    const { customFields: { sections = [] } = {} } = type;
    sections?.forEach?.((section) => {
      getCustomFieldsForSection(acc, section);
    });
    return acc;
  }, {})
);

const configure = ({
  id,
  sectionId,
  sections,
  setConfigProps,
  configProps = {},
  configState = {},
  setConfigState,
  setCanSubmit,
  formattedCustomTables = [],
  customTables = {},
  isExternalForm,
  isMultiDivisionForm = false,
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  disableOptional,
  t,
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  costcodesLoading = false,
  contactAddressBooks = {},
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  equipmentTypes = [],
  formTemplates = [],
  labels = [],
  customFields = [],
  buckets = [],
  fullEquipmentTypes = [],
  scheduleCategories = [],
  settings = {},
  paidFlags = [],
  bucketTemplates = [],
  allowRepeatingSections = false,
  isV3Form = false,
} = {}) => {
  const {
    optional,
    columns = [],
    dataType = 'Custom',
    shouldSavePresetTableSelections = false,
    hasConditionalRendering = false,
    conditionalRenderingFormula,
    openLimit = true,
    numAnswers = 1,
    subDataType,
    shouldTransposeData = false,
    shouldShowEmptyCustomFields = true,
    warnOnConflict = false,
    attrType,
    attribute,
    linkField,
    enableWarningThreshold = false,
    warningThreshold = 0,
    linkedTimeEntryTable,
    linkedSourceEquipmentTable,
    syncHoursFromLinkedSource = false,
    autoBreakEnabled = false,
    addEquipmentTableSorting,
    enableEquipmentPrefillFromQR = false,
    groupByUser = false,
    teamBasedTimeEntries = false,
    linkProjectCostCodes = false,
    linkProjectCostCodesDropdown,
  } = configProps;

  const isTimeEntry = dataType === 'TimeEntry';
  const isEquipment = dataType === 'Equipment';
  const isAttribute = dataType === 'Attribute';
  const isMaterials = dataType === 'Materials';
  const hasCostcodeColumn = columns.some((col) => col.key === 'costcode');

  const {
    addModalVisible,
    customTextModalVisible,
  } = configState;
  const isCustom = dataType === 'Custom';

  const selectedDivisionId = divisions?.[0] ?? null;
  const usePerDivisionCustomFields = isTimeEntry && isMultiDivisionForm && divisions?.length > 1;

  const customFieldColumnOptions = usePerDivisionCustomFields
    ? {}
    : getCustomFieldOptions({
      customFields,
      selectedDivisionId,
    });

  const equipmentCustomFieldColumnOptions = getEquipmentCustomFieldOptions(fullEquipmentTypes);

  const updateAddEquipmentTableSorting = (value) => {
    setConfigProps({
      ...configProps,
      addEquipmentTableSorting: value,
    });
  };

  const projectDropdownOptions = sections.reduce((acc, section) => {
    const { fields = [], name: sectionName } = section;
    return acc.concat(
      fields.reduce((fieldAcc, field) => {
        const {
          id: fieldId,
          selectedType,
          configProps: {
            dataType: fieldDataType,
            title: fieldTitle,
          } = {},
        } = field;
        if (selectedType !== 'dropdown') return fieldAcc;
        if (fieldDataType !== 'Projects') return fieldAcc;
        return fieldAcc.concat([{
          label: `${sectionName} - ${fieldTitle}`,
          value: fieldId,
        }]);
      }, []),
    );
  }, []);

  const hasProjectDropdown = projectDropdownOptions.length > 0;

  const updateLinkProjectCostCodes = (checked) => {
    const updates = { linkProjectCostCodes: checked };
    if (!checked) {
      updates.linkProjectCostCodesDropdown = null;
    } else if (hasProjectDropdown) {
      updates.linkProjectCostCodesDropdown = projectDropdownOptions[0].value;
    }
    setConfigProps({
      ...configProps,
      ...updates,
    });
  };

  const updateLinkProjectCostCodesDropdown = (value) => {
    setConfigProps({
      ...configProps,
      linkProjectCostCodesDropdown: value,
    });
  };

  const relevantBucketTemplate = bucketTemplates.find((template) => template.id === subDataType);
  const optionsMap = getOptionsMap({
    t,
    timeEntryCustomFields: customFieldColumnOptions,
    equipmentCustomFields: equipmentCustomFieldColumnOptions,
    attrType,
    attribute,
    showExcludeAutoBreak: (
      isTimeEntry
       && autoBreakEnabled
       && settings?.autoBreakEnabled
    ),
    showNightShift: (
      isTimeEntry
      && settings?.enableNightShiftPremium
    ),
    showTimeTypeColumns: (
      isTimeEntry
      && paidFlags.includes(TIME_TYPE_FLAG)
    ),
    relevantBucketTemplate,
  });

  // Limited for now
  const attributeMap = {
    Project: {
      name: t('Project'),
      fields: [
        {
          type: 'equipment',
          name: 'Equipment',
          attr: 'equipment',
        },
      ],
    },
  };

  const setTitle = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setConfigProps({
      ...configProps,
      title: value,
    });
    setCanSubmit(value);
  };

  const updateAuthorLock = (checked) => {
    setConfigProps({
      ...configProps,
      lockEntryToAuthor: checked,
      hideAddFromTeam: checked,
    });
  };

  const updateCheckbox = (key) => (checked) => {
    if (key === 'groupByUser' && checked && isTimeEntry) {
      const hasUserColumn = columns.some((col) => col.key === 'user');
      if (!hasUserColumn) {
        const newConfig = {
          ...configProps,
          [key]: checked,
          columns: [{
            key: 'user',
            name: 'User',
          }, ...columns],
        };
        setConfigProps(newConfig);
        return;
      }
    }
    setConfigProps({
      ...configProps,
      [key]: checked,
    });
  };

  const updateConfig = (newData = {}) => {
    setConfigProps({
      ...configProps,
      ...newData,
    });
  };

  const updateLinkedTimeEntryTable = (newValue) => {
    setConfigProps({
      ...configProps,
      linkedTimeEntryTable: newValue,
      // Hours cannot come from both a time entry table and a source equipment table
      syncHoursFromLinkedSource: newValue ? false : configProps.syncHoursFromLinkedSource,
    });
  };

  const updateSyncHoursFromLinkedSource = (checked) => {
    setConfigProps({
      ...configProps,
      syncHoursFromLinkedSource: checked,
      linkedTimeEntryTable: checked ? null : configProps.linkedTimeEntryTable,
    });
  };

  const updateLinkedSourceEquipmentTable = (newValue) => {
    setConfigProps({
      ...configProps,
      linkedSourceEquipmentTable: newValue,
      hideAddNewButton: !!newValue,
      hideAddFromTeam: !!newValue,
      preloadExistingEntries: newValue ? false : configProps.preloadExistingEntries,
      syncHoursFromLinkedSource: newValue
        ? configProps.syncHoursFromLinkedSource
        : undefined,
    });
  };

  const onCustomOptionSelect = (key, nameOverride, columnDivisionId) => {
    if (columns.some((col) => col.key === key)) return;
    let nameValue;
    const {
      [dataType]: ourOptions = {},
    } = optionsMap;
    if ((dataType === 'TimeEntry' || dataType === 'Attribute') && key?.startsWith('field-')) {
      if (typeof nameOverride === 'string' && nameOverride) {
        nameValue = nameOverride;
      } else {
        const {
          [key]: {
            title,
          } = {},
        } = ourOptions;
        nameValue = title;
      }
    } else {
      const {
        [key]: newName = key,
      } = ourOptions;
      nameValue = newName;
    }
    const newColumn = {
      key,
      name: nameValue,
    };
    if (key === 'quantityRemaining') {
      newColumn.quantityRemainingMode = 'live';
    }
    if (columnDivisionId) {
      newColumn.divisionId = columnDivisionId;
    }
    const newConfig = {
      ...configProps,
      columns: columns.concat([newColumn]),
    };
    setConfigProps(newConfig);
  };

  const onCustomOptionDeselect = (key) => {
    const newConfig = {
      ...configProps,
      columns: columns.filter((item) => item.key !== key),
    };
    if (key === 'user' && isTimeEntry && groupByUser) {
      newConfig.groupByUser = false;
    }
    setConfigProps(newConfig);
  };

  const onSelect = (newDataType) => {
    const newConfig = {
      ...configProps,
      dataType: newDataType,
      columns: [],
    };
    if (newDataType in customTables) {
      const {
        [newDataType]: {
          columns: customColumns,
        } = {},
      } = customTables;
      newConfig.columns = customColumns;
    }

    newConfig.hideAddNewButton = false;
    setConfigProps(newConfig);
  };

  const setShouldSavePresetTableSelections = (val) => {
    const newConfig = {
      ...configProps,
      shouldSavePresetTableSelections: val,
    };
    setConfigProps(newConfig);
  };

  const setConditionalRenderingFormula = (formula) => {
    setConfigProps({
      ...configProps,
      conditionalRenderingFormula: formula,
    });
  };

  const setHasConditionalRendering = (checked) => {
    setConfigProps({
      ...configProps,
      hasConditionalRendering: checked,
    });
  };

  const setAttrType = (value) => {
    const typeChanged = value !== attrType;
    setConfigProps({
      ...configProps,
      attrType: value,
      linkField: typeChanged ? null : configProps.linkField,
      attribute: typeChanged ? null : configProps.attribute,
    });
  };

  const setLinkField = (value) => {
    setConfigProps({
      ...configProps,
      linkField: value,
    });
  };

  const setAttribute = (e) => {
    const {
      [attrType]: {
        fields = [],
      } = {},
    } = attributeMap;
    const field = fields.find(({ attr }) => attr === e);

    setConfigProps({
      ...configProps,
      attribute: e,
      type: field.type,
      subfields: field.subfields,
    });
  };

  const onColumnDrag = (result) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;

    const sourceDroppableId = source.droppableId;
    const destDroppableId = destination.droppableId;

    // For TimeEntry tables, prevent moving between sticky and non-sticky sections
    if (isTimeEntry && sourceDroppableId !== destDroppableId) {
      return;
    }

    const newColumns = [...columns];
    const sourceIndex = source.index;
    const destIndex = destination.index;

    // Calculate actual indices in the full columns array
    let actualSourceIndex = sourceIndex;
    let actualDestIndex = destIndex;

    if (isTimeEntry) {
      const stickyColumns = columns.filter((col) => col.isSticky);

      if (sourceDroppableId === 'sticky-columns') {
        actualSourceIndex = sourceIndex;
        actualDestIndex = destIndex;
      } else {
        actualSourceIndex = stickyColumns.length + sourceIndex;
        actualDestIndex = stickyColumns.length + destIndex;
      }
    }

    const [movedColumn] = newColumns.splice(actualSourceIndex, 1);
    newColumns.splice(actualDestIndex, 0, movedColumn);

    setConfigProps({
      ...configProps,
      columns: newColumns,
    });
  };

  // If the addEquipmentTableSorting is not set, set it to name-asc
  if (isEquipment && !addEquipmentTableSorting) {
    setConfigProps({
      ...configProps,
      addEquipmentTableSorting: 'name-asc',
    });
  }

  let {
    [dataType]: options = {},
  } = optionsMap;
  if (dataType in customTables) {
    const {
      [dataType]: {
        columns: tableColumns = [],
      } = {},
    } = customTables;
    options = {};
    tableColumns.forEach((col) => {
      options[col.key] = col.name;
    });
  }

  const onTitleChanged = ({ id: colKey, title }) => {
    const editingCol = columns.find((col) => col.key === colKey);
    if (editingCol?.isCustomText) {
      const error = validateCustomTextColumnName(title, {
        nativeOptions: options,
        columns,
        excludeKey: colKey,
      });
      if (error) {
        message.error(error);
        return;
      }
    }
    const newColumns = columns.map((col) => {
      if (col.key !== colKey) return col;
      return {
        ...col,
        name: title,
        originalName: col.originalName || col.name,
      };
    });
    setConfigProps({
      ...configProps,
      columns: newColumns,
    });
  };

  const validateCustomTextName = (title, colKey) => validateCustomTextColumnName(title, {
    nativeOptions: options,
    columns,
    excludeKey: colKey,
  });

  const relevantTypes = [...types];
  if (isBoardCards && allowRepeatingSections) {
    relevantTypes.push(bucketType);
  }

  let dataTypes = relevantTypes.concat(formattedCustomTables);
  if (isExternalForm) {
    dataTypes = [relevantTypes.find(({ key }) => key === 'Custom')];
  } else if (isMultiDivisionForm) {
    dataTypes = relevantTypes.filter(({ key }) => dataTypesAllowedInMultiDivisionTable.has(key));
  }

  const onAddCalculationColumn = (key) => {
    const existing = new Set(
      columns.map((col) => col.key),
    );
    if (existing.has(key)) {
      return;
    }
    const newConfig = {
      ...configProps,
      columns: columns.concat([{
        key,
        name: key,
        isCalculation: true,
      }]),
    };
    setConfigProps(newConfig);
    setConfigState({ ...configState, addModalVisible: false });
  };

  const calcColumnValidator = (newValue) => {
    const existing = new Set(
      columns.map((col) => col.key),
    );
    if (existing.has(newValue)) {
      return 'Column name must be unique';
    }
    return null;
  };

  const onAddCustomTextColumn = (name, customDataType) => {
    const newConfig = {
      ...configProps,
      columns: columns.concat([createCustomTextColumn(name, customDataType)]),
    };
    setConfigProps(newConfig);
    setConfigState({ ...configState, customTextModalVisible: false });
  };

  const customTextColumnValidator = (newValue) => validateCustomTextColumnName(newValue, {
    nativeOptions: options,
    columns,
  });

  const onNumberChange = (num) => {
    const newConfig = {
      ...configProps,
      numAnswers: num,
    };
    setConfigProps(newConfig);
  };

  const onWarningThresholdChange = (num) => {
    const newConfig = {
      ...configProps,
      warningThreshold: num,
    };
    setConfigProps(newConfig);
  };

  const relevantTimeEntryTables = [];
  const relevantEquipmentTables = [];
  let isLinkedEquipmentSource = false;

  if (isEquipment) {
    sections.forEach(( { fields = [], name: sectionName }) => {
      fields.forEach((field) => {
        if (field.selectedType !== 'table') return;
        if (field.configProps?.dataType === 'TimeEntry') {
          relevantTimeEntryTables.push({ value: field.id, label: `${sectionName} - ${field.configProps.title}` });
        }
        // Only add equipment tables that are not the current table
        if (field.configProps?.dataType === 'Equipment' && field.id !== id) {
          relevantEquipmentTables.push({ value: field.id, label: `${sectionName} - ${field.configProps.title}` });
        }
        // Check if the current table is already a source for another table (prevent loops)
        if (id && field.configProps?.linkedSourceEquipmentTable === id) {
          isLinkedEquipmentSource = true;
        }
      });
    });
  }

  const ourType = relevantTypes.find((type) => type.key === dataType);
  const {
    hasSubDataType = false,
    subDataTypePlaceholder,
    subDataTypeSelectMode,
  } = ourType ?? configProps;

  const onSubDataTypeSelect = (newSubDataType) => {
    const newConfig = {
      ...configProps,
      subDataType: newSubDataType,
    };

    setConfigProps(newConfig);
  };

  const subDataTypeMap = {
    Equipment: equipmentTypes,
    Buckets: bucketTemplates.map((template) => ({
      value: template.id,
      label: template.name,
    })),
  };

  const subDataTypes = hasSubDataType
    ? subDataTypeMap[dataType]
    : [];

  const subDataTypeSet = new Set(subDataTypes?.map?.((type) => type.value));

  const filteredSubType = subDataTypeSelectMode === 'multiple'
      ? subDataType?.filter?.((type) => subDataTypeSet.has(type)) ?? []
      : subDataType;

  const attributeOptions = [{
    name: `${t('Project')}`,
    value: 'Project', // For now just Project - Equipment
  }];
  const linkOptions = isAttribute ? getLinkOptions({ dataType: attrType, sections }) : [];
  return (
    <div>
      <Row className="form-required-field">
        Title:
      </Row>
      <Row style={{ marginTop: 5 }}>
        <OnTraccrTextInput
          textarea
          placeholder="Insert title here"
          onChange={setTitle}
          value={configProps.title}
        />
      </Row>
      <OptionalRow
        onChange={updateCheckbox('optional')}
        optional={optional}
        disabled={disableOptional}
      />
      <ConditionalRenderingRow
        onFormulaChange={setConditionalRenderingFormula}
        conditionalRenderingFormula={conditionalRenderingFormula}
        onChange={setHasConditionalRendering}
        hasConditionalRendering={hasConditionalRendering}
        sections={sections}
        id={id}
        customers={customers}
        projects={projects}
        users={users}
        costcodes={costcodes}
        phases={phases}
        projectIdMap={projectIdMap}
        vendors={vendors}
        equipment={equipment}
        formTemplates={formTemplates}
        labels={labels}
        contactAddressBooks={contactAddressBooks}
        buckets={buckets}
        scheduleCategories={scheduleCategories}
      />
      <HideIfEmptyInPDFCheckbox
        isV3Form={isV3Form}
        configProps={configProps}
        setConfigProps={setConfigProps}
      />
      {(configProps.dataType !== 'Custom' && configProps.dataType !== 'Shifts' && !isAttribute) && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={configProps.preventEdits}
          onChange={updateCheckbox('preventEdits')}
          label="Prevent data edits"
          hoverNode={(
            <div style={{ width: 300 }}>
              Check this box to prevent edits to data in the table.
              This will prevent users from modifying entries pulled into the table.
            </div>
          )}
        />
      )}
      { (configProps.dataType !== 'Custom' && configProps.dataType !== 'Shifts' && configProps.dataType !== 'Buckets') && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={configProps.hideAddNewButton}
          onChange={updateCheckbox('hideAddNewButton')}
          disabled={!!linkedSourceEquipmentTable}
          label="Hide Add New Button"
          hoverNode={(
            <div style={{ width: 300 }}>
              Check this box to hide the Add New button from the table.
              This will prevent users from adding new items to the table.
            </div>
          )}
        />
      )}
      {(isTimeEntry || isEquipment)
        && (
          <OnTraccrCheckbox
            style={{ marginTop: 10 }}
            value={configProps.hideAddFromTeam}
            onChange={updateCheckbox('hideAddFromTeam')}
            disabled={(isTimeEntry && configProps.lockEntryToAuthor) || !!linkedSourceEquipmentTable}
            label="Hide Add from Team Button"
            hoverNode={(
              <div style={{ width: 300 }}>
                Check this box to hide the Add from Team button from the table.
              </div>
            )}
          />
        )}
      { isEquipment && !isExternalForm && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={enableEquipmentPrefillFromQR}
          onChange={updateCheckbox('enableEquipmentPrefillFromQR')}
          label="Pre-fill table with QR scanned equipment?"
          hoverNode={(
            <div style={{ width: 250 }}>
              Check this box if you want this table to be pre-filled with the equipment scanned from QR code
            </div>
          )}
        />
      )}
      {
        isTimeEntry && (
          <OnTraccrCheckbox
            style={{ marginTop: 10 }}
            value={warnOnConflict}
            label="Warn user of time conflicts"
            onChange={updateCheckbox('warnOnConflict')}
            hoverNode="Check this box if you want to show a warning when a user enters time that conflicts with existing time entries"
          />
        )
      }
      {
        isTimeEntry && (
          <OnTraccrCheckbox
            style={{ marginTop: 10 }}
            value={configProps.preventNonCurrentDateSubmission}
            onChange={updateCheckbox('preventNonCurrentDateSubmission')}
            label="Prevent submission for non-current date entries"
            hoverNode="Check this box to prevent form submission when time entry dates are not today's date"
          />
        )
      }
      {
        isTimeEntry && (
          <OnTraccrCheckbox
            style={{ marginTop: 10 }}
            value={teamBasedTimeEntries}
            onChange={updateCheckbox('teamBasedTimeEntries')}
            label="Team Based Time Entries"
            hoverNode="Check this box to allow users to enter time entries for their team.  This will allow users to apply the same time entries to multiple users."
          />
        )
      }
      {isTimeEntry && !isBoardCards && !isExternalForm && !isMultiDivisionForm
        && (
          <OnTraccrCheckbox
            style={{ marginTop: 10 }}
            value={configProps.lockEntryToAuthor}
            onChange={updateAuthorLock}
            label="Lock time entry to form author"
            hoverNode={(
              <div style={{ width: 300 }}>
                Check this box to lock time entry to form author.
              </div>
            )}
          />
        )}
      {isTimeEntry
      && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={groupByUser}
          onChange={updateCheckbox('groupByUser')}
          label="Group entries by user"
          hoverNode={(
            <div style={{ width: 300 }}>
              Check this box to group time entries by user with summary headers
            </div>
          )}
        />
      )}
      {isTimeEntry && !!settings?.autoBreakEnabled && settings?.autoBreakMode === 'Subtract' && (
        <OnTraccrCheckbox
          style={{ marginTop: 10 }}
          value={autoBreakEnabled}
          onChange={updateCheckbox('autoBreakEnabled')}
          label="Add auto break?"
          hoverNode={(
            <div style={{ width: 300 }}>
              Check this box to use your company auto break settings
            </div>
          )}
        />
      )}
      { isTimeEntry && (
        <Row style={{ marginTop: 15 }} align="middle" gutter={20}>
          <Col style={{ height: 32 }}>
            <OnTraccrNumberInput
              style={{ width: 100 }}
              value={warningThreshold}
              min={1}
              max={undefined}
              onChange={onWarningThresholdChange}
              disabled={!enableWarningThreshold}
            />
          </Col>
          <Col style={{ height: 32 }}>
            <OnTraccrCheckbox
              style={{ height: '100%', alignItems: 'center' }}
              value={enableWarningThreshold}
              onChange={updateCheckbox('enableWarningThreshold')}
              label="Enable Warning Threshold (Hours)?"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Check this box if you want entries that exceed this time threshold to display a warning on the form.
                </div>
              )}
              hoverIconProps={{ style: { marginTop: 0 } }}
            />
          </Col>
        </Row>
      )}
      <Row className="form-required-field" style={{ marginTop: 15 }}>
        Data Type:
      </Row>
      <Row style={{ marginTop: 5 }}>
        <Col>
          <Select
            style={{ width: 350 }}
            placeholder="Select Data Type"
            onSelect={onSelect}
            value={configProps.dataType}
            showSearch
            listHeight={400}
          >
            {
            dataTypes.map((type) => (
              <Select.Option value={type.key} key={type.title}>
                {fieldOption(type)}
              </Select.Option>
            ))
          }
          </Select>
          <DropdownFieldSubDataTypeSelect
            placeholder={subDataTypePlaceholder ?? `Select ${dataType} Type`}
            onChange={onSubDataTypeSelect}
            value={filteredSubType}
            mode={subDataTypeSelectMode}
            options={subDataTypes}
          />
          {isAttribute && (
            <Row style={{ marginTop: 5 }}>
              <Select
                style={{ width: 350 }}
                placeholder="Select attribute instance type"
                onChange={setAttrType}
                value={attrType}
                optionFilterProp="label"
              >
                {attributeOptions.map((item) => (
                  <Select.Option
                    value={item.value}
                    key={item.value}
                    label={item.name}
                  >
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Row>
          )}
          {isAttribute && attrType && (
            <>
              <Row style={{ marginTop: 5 }}>
                <Select
                  style={{ width: 350 }}
                  placeholder={`Select attribute for ${attributeMap[attrType]?.name ?? attrType}`}
                  onChange={setAttribute}
                  value={attribute}
                  optionFilterProp="label"
                >
                  {attributeMap[attrType]?.fields.map((item) => (
                    <Select.Option
                      value={item.attr}
                      key={item.attr}
                      label={item.attr}
                    >
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Row>
              <Row style={{ marginTop: 5 }}>
                <Select
                  style={{ width: 350 }}
                  placeholder="Link to field"
                  onChange={setLinkField}
                  value={linkField}
                  optionFilterProp="label"
                  options={linkOptions}
                  allowClear
                />
              </Row>
            </>
          )}
        </Col>
      </Row>
      <div>
        <Row className="form-required-field" style={{ marginTop: 15 }} gutter={20}>
          <Col>
            Columns:
          </Col>
          <Col>
            <HoverHelp
              placement="topRight"
              content={(
                <div style={{ width: 300 }}>
                  Columns should be entered in the order you want them to appear (left to right),
                  but can be rearranged below.
                </div>
              )}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 10, marginLeft: 0, marginRight: 0 }}>
          {usePerDivisionCustomFields ? (
            <TimeEntryGroupedColumnSelect
              generalOptions={options}
              divisions={divisions}
              customFields={customFields}
              columns={columns}
              onSelect={onCustomOptionSelect}
              onDeselect={onCustomOptionDeselect}
              isCustom={isCustom}
            />
          ) : (
            <Select
              style={{ width: 350 }}
              mode={isCustom ? 'tags' : 'multiple'}
              tokenSeparators={[',']}
              placeholder="Enter column names"
              onSelect={onCustomOptionSelect}
              onDeselect={onCustomOptionDeselect}
              value={columns.map((col) => col.key)}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...isCustom ? { open: false } : {}}
            >
              {
                Object.keys(options).map((opt) => (
                  <Select.Option value={opt} key={opt}>
                    {(isTimeEntry || isAttribute) && opt?.startsWith('field-') ? options[opt]?.label : options[opt]}
                  </Select.Option>
                ))
              }
              {
                columns
                  .filter((col) => !(col.key in options))
                  .map((col) => (
                    <Select.Option value={col.key} key={col.key}>
                      {col.name}
                    </Select.Option>
                  ))
              }
            </Select>
          )}
          <OnTraccrButton
            title="Add Calculation"
            icon={<PlusOutlined />}
            onClick={() => setConfigState({
              ...configState,
              addModalVisible: true,
            })}
            style={{ marginLeft: 15 }}
          />
          {!isCustom && (
            <OnTraccrButton
              title="Add Custom Column"
              icon={<PlusOutlined />}
              onClick={() => setConfigState({
                ...configState,
                customTextModalVisible: true,
              })}
              style={{ marginLeft: 15 }}
            />
          )}
        </Row>
        <Row style={{ marginTop: 5, marginLeft: 0, marginRight: 0 }} gutter={10}>
          {isTimeEntry ? (
            <TimeEntryColumnsDragDrop
              columns={columns}
              onColumnDrag={onColumnDrag}
              onCustomOptionDeselect={onCustomOptionDeselect}
              configProps={configProps}
              setConfigProps={setConfigProps}
              fieldId={id}
              sectionId={sectionId}
              sections={sections}
              dataType={dataType}
              divisions={divisions}
              onTitleChanged={onTitleChanged}
              validateCustomTextName={validateCustomTextName}
            />
          ) : (
            <DragDropContext onDragEnd={onColumnDrag}>
              <Droppable droppableId="parent">
                {({ droppableProps, innerRef, placeholder }) => (
                  <div
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...droppableProps}
                    ref={innerRef}
                    style={{ width: '100%' }}
                  >
                    {
                      columns.map((col, idx) => (
                        <TableFieldColumnCard
                          id={col.key}
                          orderIndex={idx}
                          originalName={col.originalName || col.name}
                          name={col.name}
                          isCalculation={col.isCalculation}
                          isCustomText={col.isCustomText}
                          isReadOnly={col.isReadOnly}
                          onDeleteClicked={() => { onCustomOptionDeselect(col.key); }}
                          key={col.key}
                          configProps={configProps}
                          setConfigProps={setConfigProps}
                          fieldId={id}
                          sectionId={sectionId}
                          sections={sections}
                          dataType={dataType}
                          divisions={divisions}
                          onTitleChanged={onTitleChanged}
                          validateCustomTextName={validateCustomTextName}
                        />
                      ))
                    }
                    {placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Row>
        <Row>
          <OnTraccrCheckbox
            style={{ marginTop: 15 }}
            value={!disableOptional && configProps.requiredColumns}
            onChange={updateCheckbox('requiredColumns')}
            disabled={disableOptional}
            label="Required Columns"
            hoverNode={(
              <div style={{ width: 250 }}>
                Check this box to make all columns
                {' '}
                <b>required</b>
                .
                <br />
                <br />
                Required columns cannot be left blank by a user filling out the form
              </div>
            )}
          />
        </Row>
        {(isMaterials || isEquipment) && hasCostcodeColumn && (
          <>
            <Row>
              <OnTraccrCheckbox
                style={{ marginTop: 15 }}
                value={linkProjectCostCodes}
                onChange={updateLinkProjectCostCodes}
                label="Link to Project Cost Codes"
                disabled={!hasProjectDropdown}
                hoverNode={(
                  <div style={{ width: 300 }}>
                    Check this box to show only cost codes linked to the selected project.
                    To enable this option, you must first add a project dropdown field to the form.
                  </div>
                )}
              />
            </Row>
            {linkProjectCostCodes && hasProjectDropdown && (
              <Row style={{ marginTop: 10 }}>
                <Select
                  style={{ width: 500 }}
                  options={projectDropdownOptions}
                  optionFilterProp="label"
                  allowClear
                  showSearch
                  placeholder="Select a Project Dropdown Field"
                  value={linkProjectCostCodesDropdown}
                  onChange={updateLinkProjectCostCodesDropdown}
                />
              </Row>
            )}
          </>
        )}
        {isEquipment && (
          <>
            <Row style={{ marginTop: 10 }} gutter={10}>
              <Col>
                &quot;Add Equipment&quot; Table Sorting
              </Col>
              <Col>
                <HoverHelp
                  placement="topRight"
                  content={(
                    <div style={{ width: 300 }}>
                      Set the sorting criteria for the equipment table
                      when a user adds a new row with &quot;Add Equipment&quot; button.
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row style={{ marginTop: 10 }}>
              <Select
                style={{ width: 350 }}
                placeholder="Select Sorting Type"
                onChange={updateAddEquipmentTableSorting}
                value={addEquipmentTableSorting}
                optionFilterProp="label"
                options={[
                  { label: 'Name: A-Z', value: 'name-asc' },
                  { label: 'Name: Z-A', value: 'name-desc' },
                  { label: 'ID: Ascending', value: 'id-asc' },
                  { label: 'ID: Descending', value: 'id-desc' },
                ]}
                allowClear
              />
            </Row>
          </>
        )}
        {isEquipment && !!relevantEquipmentTables.length && (
          <>
            <Row style={{ marginTop: 10 }} gutter={10}>
              <Col>
                Linked Source Equipment Table
              </Col>
              <Col>
                <HoverHelp
                  placement="topRight"
                  content={(
                    <div style={{ width: 300 }}>
                      Select another Equipment table to link this table to.
                      This table will automatically be populated with the equipment
                      selected in the source table, so users do not have to select
                      the same equipment twice. If the source table is in a section
                      that users can duplicate, equipment from every copy of that
                      section is included.
                      <br />
                      <br />
                      Users will not be able to add or delete rows in this table,
                      but they can still edit the remaining columns.
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row style={{ marginTop: 10 }} align="middle">
              <Select
                style={{ width: 350 }}
                placeholder="Select Equipment Table"
                onChange={updateLinkedSourceEquipmentTable}
                value={linkedSourceEquipmentTable}
                optionFilterProp="label"
                options={relevantEquipmentTables}
                allowClear
                disabled={isLinkedEquipmentSource}
              />
              {isLinkedEquipmentSource && (
                <Popover
                  placement="bottomLeft"
                  trigger="hover"
                  content={(
                    <div style={{ width: 300 }}>
                      This table is used as a source by another equipment table,
                      so this link cannot be changed. Unlink the other table first
                      to edit this setting.
                    </div>
                  )}
                  title="Warning"
                >
                  <WarningOutlined
                    style={{
                      color: Colors.ONTRACCR_DARK_YELLOW,
                      marginLeft: 10,
                    }}
                  />
                </Popover>
              )}
            </Row>
            {!!linkedSourceEquipmentTable && (
              <OnTraccrCheckbox
                style={{ marginTop: 10 }}
                value={syncHoursFromLinkedSource}
                onChange={updateSyncHoursFromLinkedSource}
                label="Map Hours from Source Table"
                hoverNode={(
                  <div style={{ width: 300 }}>
                    When checked, the Hours, Days, Weeks and Months entered in
                    the source table are copied into this table and kept in sync.
                    Users will not be able to edit those columns in this table.
                    Uncheck to let users enter these values in this table separately.
                    <br />
                    <br />
                    This cannot be combined with a linked Time Entry table.
                    Checking this box clears that link.
                  </div>
                )}
              />
            )}
          </>
        )}
        {dataType === 'TimeEntry' && (
          <>
            <Row>
              <OnTraccrCheckbox
                style={{ marginTop: 15 }}
                value={shouldTransposeData}
                onChange={updateCheckbox('shouldTransposeData')}
                label="Vertical Layout"
                hoverNode={(
                  <div style={{ width: 300 }}>
                    Check this box to display the table in a vertical layout.
                    Time entries will be displayed in the columns
                    and the fields will be displayed in the rows.
                  </div>
                )}
              />
            </Row>
            <Row>
              <OnTraccrCheckbox
                style={{ marginTop: 15 }}
                value={shouldShowEmptyCustomFields}
                onChange={updateCheckbox('shouldShowEmptyCustomFields')}
                label="Show Empty Custom Fields?"
                hoverNode={(
                  <div style={{ width: 300 }}>
                    Check this box to show custom fields columns that have no data.
                  </div>
                )}
              />
            </Row>
          </>
        )}
        {!linkedSourceEquipmentTable && (
          <TablePreload
            configProps={configProps}
            updateConfig={updateConfig}
            divisions={divisions}
            costcodes={costcodes}
            costcodesLoading={costcodesLoading}
            sections={sections}
            templateId={templateId}
          />
        )}
        <TableAutofill
          configProps={configProps}
          updateConfig={updateConfig}
          sections={sections}
          id={id}
          dataType={dataType}
        />
        <TableScheduleReminder
          configProps={configProps}
          updateConfig={updateConfig}
          sections={sections}
          dataType={dataType}
        />
        { isEquipment && !!relevantTimeEntryTables?.length && (
        <>
          <Row style={{ marginTop: 10 }} gutter={10}>
            <Col>
              Link Equipment Table to Time Entry Table
            </Col>
            <Col>
              <HoverHelp
                placement="topRight"
                content={(
                  <div style={{ width: 300 }}>
                    Select the Time Entry table that you would like to link to this Equipment table.
                    It will link the hours entered in the Time Entry table to the Equipment table based on the assigned user.
                  </div>
                )}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 10 }}>
            <Select
              style={{ width: 350 }}
              placeholder="Select Time Entry Table"
              onChange={updateLinkedTimeEntryTable}
              value={linkedTimeEntryTable}
              optionFilterProp="label"
              options={relevantTimeEntryTables}
              allowClear
            />
          </Row>
        </>
      )}
        {!isAttribute && (
          <>
            <Row style={{ marginTop: 15 }} gutter={20}>
              <Col>
                <OnTraccrSwitch
                  value={shouldSavePresetTableSelections && !configProps.preloadExistingEntries}
                  onChange={setShouldSavePresetTableSelections}
                  disabled={configProps.preloadExistingEntries}
                  title="Preset Table Selections:"
                />
              </Col>
              <Col>
                <HoverHelp
                  placement="topRight"
                  content={(
                    <div style={{ width: 300 }}>
                      Use this option to select default rows for the table
                      which will appear every time a user completes the form.
                      <br />
                      <br />
                      Once this toggle is enabled, use the field preview underneath
                      to add your preset selections into the table.
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row style={{ marginTop: 15 }} align="middle" gutter={20}>
              <Col style={{ height: 32 }}>
                <OnTraccrNumberInput
                  style={{ width: 100 }}
                  value={numAnswers}
                  min={1}
                  max={undefined}
                  onChange={onNumberChange}
                  disabled={openLimit}
                />
              </Col>
              <Col style={{ height: 32 }}>
                <OnTraccrCheckbox
                  style={{ height: '100%', alignItems: 'center' }}
                  value={openLimit}
                  onChange={updateCheckbox('openLimit')}
                  label="Leave open?"
                  hoverNode="Check this box if you want to remove restrictions on the number of answers"
                  hoverIconProps={{ style: { marginTop: 0 } }}
                />
              </Col>
            </Row>
          </>
        )}
        {!isBoardCards && !isExternalForm && !isMultiDivisionForm && (
          <FieldTriggerCheckbox
            onChange={updateCheckbox('fieldTrigger')}
            onEditableChange={setFieldTriggerEditable}
            sections={sections}
            projectId={projectId}
            templateId={templateId}
            divisions={divisions}
            configProps={configProps}
            setConfigProps={setConfigProps}
            name={name}
          />
        )}
      </div>
      <SimpleTextInputModal
        title="Add Calculation Column"
        visible={addModalVisible}
        onClose={() => setConfigState({
          ...configState,
          addModalVisible: false,
        })}
        validator={calcColumnValidator}
        onSave={onAddCalculationColumn}
        placeholder="Column Name"
      />
      <CustomColumnModal
        title="Add Custom Column"
        visible={customTextModalVisible}
        onClose={() => setConfigState({
          ...configState,
          customTextModalVisible: false,
        })}
        validator={customTextColumnValidator}
        onSave={onAddCustomTextColumn}
        placeholder="Column Name"
      />
    </div>
  );
};

const preview = ({
  setPreviewProps,
  configProps = {},
  previewProps = {},
  projects = [],
  materials = [],
  projectMaterials,
  equipment = [],
  equipmentTypes = [],
  changeOrderMap = {},
  isDisplay,
  id,
  responses = {},
  setResponses,
  responding = false,
  customerId,
  projectId,
  customTables = {},
  divisions,
  sections,
  templateId,
  showCondensedView,
  locked,
  t,
  inAddDrawer,
  createTimeTableIds,
  // Inline Editing Props for Time Entry Table
  vendorId,
  setCustomerIds,
  setProjectIds,
  setVendorIds,
  setContactIds,
  setCostcodeIds,
  setSubContractIds,
  setUserIds,
  setBucketId,
  userToLabel = {},
  customerToLabel = {},
  vendorToLabel = {},
  subContractMap = {},
  subContractCoMap = {},
  scheduleOfValues = {},
  selectedBucket,
  selectedBucketTypeToIdMap,
  selectedBucketTypes,
  projectEquipment,
  equipmentTypeMap,
  projectIds = [],
  timecardProjectId,
  timecardLabourCostcodeKey = null,
  timecardLabourPhaseId = null,
  poCloseMode = false,
  onFieldTrigger,
  costcodes = [],
  costcodeIdMap: costcodeMap = {},
  fetchProjectCostcodes,
  buckets = [],
  bucketTemplates = [],
  statuses = {},
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
  enrichTimeEntryCustomData,
} = {}) => {
  const {
    optional,
    title = 'Title goes here',
    dataType,
    fieldDataType, // used by dynamic attribute
    subDataType,
    columns = [],
    fieldTrigger,
  } = configProps;

  const isCustomSavedFormTable = dataType in customTables || fieldDataType in customTables;

  // Only load in preset data if there is no response
  // Preset data is unique to tables so we don't want to overwrite other responses
  if ((!responses[id] || !responses[id].values) && setResponses && configProps.presetData) {
    setResponses((prev) => ({
      ...prev,
      [id]: {
        values: configProps.presetData.selected || [],
      },
    }));
  }

  // Equipment Filtered by Type
  const equipmentTypeSet = new Set(equipmentTypes.map((type) => type.id));
  const subDataTypeSet = new Set(
    subDataType?.filter?.((typeId) => equipmentTypeSet.has(typeId) || typeId === null) ?? [],
  );
  const filteredEquipment = equipment.filter((eq) => (
    eq.active
    && (!subDataTypeSet?.size || subDataTypeSet.has(eq.equipmentTypeId))
  ));

  const tableTitle = (
    <TableFieldTitle
      title={title}
      optional={optional}
      fieldTrigger={fieldTrigger}
      isDisplay={isDisplay}
      sections={sections}
      templateId={templateId}
      projectId={projectId}
      divisions={divisions}
      configProps={configProps}
      responding={responding}
      onFieldTrigger={onFieldTrigger}
    />
  );

  return (
    <div style={{ pointerEvents: 'auto' }}>
      { dataType === 'Materials' && (
        <MaterialTablePreview
          title={tableTitle}
          materials={materials}
          projectMaterials={projectMaterials}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          customerId={customerId}
          projectId={projectId}
          configProps={configProps}
          showCondensedView={showCondensedView}
          costcodes={costcodes}
          divisions={divisions}
          onOpenFile={onOpenFile}
          setCurrFileFieldId={setCurrFileFieldId}
          setCurrAttachmentRowId={setCurrAttachmentRowId}
          setOpenFileIndex={setOpenFileIndex}
          setCurrFieldId={setCurrFieldId}
          timecardLabourCostcodeKey={timecardLabourCostcodeKey}
          timecardLabourPhaseId={timecardLabourPhaseId}
        />
      )}

      { dataType === 'Labour' && (
        <LabourTablePreview
          title={tableTitle}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          customerId={customerId}
          projectId={projectId}
          configProps={configProps}
          showCondensedView={showCondensedView}
          divisions={divisions}
        />
      )}
      { dataType === 'ChangeOrder' && (
        <ChangeOrderTablePreview
          title={tableTitle}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          changeOrderMap={changeOrderMap}
          configProps={configProps}
          showCondensedView={showCondensedView}
        />
      )}
      { dataType === 'Shifts' && (
        <ShiftTablePreview
          title={tableTitle}
          projectId={projectId}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          changeOrderMap={changeOrderMap}
          configProps={configProps}
          showCondensedView={showCondensedView}
          projects={projects}
          projectLocked={locked}
          inAddDrawer={inAddDrawer}
          costcodes={costcodes}
          sections={sections}
          onOpenFile={onOpenFile}
          setCurrFileFieldId={setCurrFileFieldId}
          setCurrAttachmentRowId={setCurrAttachmentRowId}
          setOpenFileIndex={setOpenFileIndex}
          setCurrFieldId={setCurrFieldId}
        />
      )}
      { dataType === 'TimeEntry' && (
        <TimeEntryTablePreview
          title={tableTitle}
          projectId={projectId}
          columns={columns}
          sections={sections}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          customerId={customerId}
          changeOrderMap={changeOrderMap}
          divisions={divisions}
          configProps={configProps}
          showCondensedView={showCondensedView}
          t={t}
          templateId={templateId}
          createTimeTableIds={createTimeTableIds}
          subContractMap={subContractMap}
          subContractCoMap={subContractCoMap}
          scheduleOfValues={scheduleOfValues}
          customerToLabel={customerToLabel}
          userToLabel={userToLabel}
          vendorToLabel={vendorToLabel}
          setCustomerIds={setCustomerIds}
          setProjectIds={setProjectIds}
          setVendorIds={setVendorIds}
          setContactIds={setContactIds}
          setCostcodeIds={setCostcodeIds}
          setSubContractIds={setSubContractIds}
          setUserIds={setUserIds}
          setBucketId={setBucketId}
          vendorId={vendorId}
          selectedBucket={selectedBucket}
          selectedBucketTypeToIdMap={selectedBucketTypeToIdMap}
          selectedBucketTypes={selectedBucketTypes}
          costcodes={costcodes}
          costcodeMap={costcodeMap}
          fetchProjectCostcodes={fetchProjectCostcodes}
          inAddDrawer={inAddDrawer}
          enrichTimeEntryCustomData={enrichTimeEntryCustomData}
          poCloseMode={poCloseMode}
        />
      )}
      { isCustomSavedFormTable && (
        <CustomSavedFormTablePreview
          title={tableTitle}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          configProps={configProps}
          customTables={customTables}
          showCondensedView={showCondensedView}
        />
      )}
      { dataType === 'Custom' && (
        <CustomTablePreview
          title={tableTitle}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          configProps={configProps}
          showCondensedView={showCondensedView}
        />
      )}
      {dataType === 'Equipment' && (
        <EquipmentTablePreview
          title={tableTitle}
          equipment={filteredEquipment}
          projectBillingRateProjectId={projectIds?.[0] ?? projectId}
          projectId={projectId}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          configProps={configProps}
          showCondensedView={showCondensedView}
          divisions={divisions}
          costcodes={costcodes}
          onOpenFile={onOpenFile}
          setCurrFileFieldId={setCurrFileFieldId}
          setCurrAttachmentRowId={setCurrAttachmentRowId}
          setOpenFileIndex={setOpenFileIndex}
          setCurrFieldId={setCurrFieldId}
          timecardLabourCostcodeKey={timecardLabourCostcodeKey}
          timecardLabourPhaseId={timecardLabourPhaseId}
        />
      )}
      {dataType === 'CostCodes' && (
        <CostCodeTablePreview
          title={tableTitle}
          id={id}
          configProps={configProps}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          responding={responding}
          responses={responses}
          setResponses={setResponses}
          isDisplay={isDisplay}
          showCondensedView={showCondensedView}
          projectIds={timecardProjectId ? [timecardProjectId] : projectIds}
          costcodes={costcodes}
          costcodeMap={costcodeMap}
        />
      )}
      { dataType === 'Attribute' && (
        <AttributeTablePreview
          title={tableTitle}
          equipment={equipment}
          divisions={divisions}
          equipmentTypeMap={equipmentTypeMap}
          projectId={projectId}
          projects={projects}
          projectEquipment={projectEquipment}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          configProps={configProps}
          showCondensedView={showCondensedView}
        />
      )}
      { dataType === 'Buckets' && (
        <BucketTablePreview
          title={tableTitle}
          buckets={buckets}
          bucketTemplates={bucketTemplates}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          configProps={configProps}
          showCondensedView={showCondensedView}
          statuses={statuses}
        />
      )}
      { dataType === 'ScheduleOfValues' && (
        <ScheduleOfValuesTablePreview
          title={tableTitle}
          columns={columns}
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
          isDisplay={isDisplay}
          id={id}
          responses={responses}
          setResponses={setResponses}
          responding={responding}
          configProps={configProps}
          showCondensedView={showCondensedView}
        />
      )}
    </div>
  );
};

export default {
  configure,
  preview,
  title: 'Table',
  description: 'User can enter data into a table',
};
